/**
 * Untagged product group pool helpers (branch-scoped pool_nwt / pool_pcs only).
 */

export function numPool(value, fallback = 0) {
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : fallback;
}

export function formatPoolShort(poolNwt, poolPcs) {
  return `${numPool(poolNwt).toFixed(3)} g (nwt) | ${numPool(poolPcs)} pcs`;
}

/** Dropdown / input display — name only (no pool suffix). */
export function productGroupOptionDisplayName(option = {}) {
  if (!option) return "";
  if (option.name) return String(option.name);
  const label = String(option.label || "");
  const poolIdx = label.indexOf(" — pool:");
  return poolIdx >= 0 ? label.slice(0, poolIdx).trim() : label;
}

export function formatProductGroupPoolLabel(group = {}) {
  const nwt = numPool(group.pool_nwt ?? group.poolNwt);
  const pcs = numPool(group.pool_pcs ?? group.poolPcs);
  return `Available (untagged): ${nwt.toFixed(3)} g (nwt) / ${pcs} pcs`;
}

/** Pool deduction for tagging (create_single / create_bulk): nwt × pcs + pcs count. */
export function poolDeductionForTaggingLine({ nwt, gwt, pcs } = {}) {
  const p = numPool(pcs);
  const n = numPool(nwt) || numPool(gwt);
  const totalNwt = n > 0 && p > 0 ? n * p : n;
  return { pcs: p, nwt: totalNwt };
}

/** Refinery line: fine_wt (nwt) + 1 pc from pool per API contract. */
export function poolDeductionForRefineryLine({ fine_wt, nwt } = {}) {
  const wt = numPool(fine_wt) || numPool(nwt);
  return { pcs: 1, nwt: wt, fine_wt: wt };
}

/**
 * Aggregate deductions by product_group_id and compare to pool balances.
 * @param {Array<{ product_group_id, pcs?, nwt?, fine_wt?, label? }>} lines
 * @param {Record<number, { pool_nwt, pool_pcs, name? }>} poolByGroupId
 */
export function validatePoolDeductions(lines, poolByGroupId, options = {}) {
  const mode = options.mode || "tagging";
  const totals = {};

  for (const line of lines) {
    const gid = Number(line.product_group_id);
    if (!Number.isFinite(gid) || gid <= 0) continue;

    if (!totals[gid]) {
      totals[gid] = { pcs: 0, nwt: 0, fine_wt: 0 };
    }

    if (mode === "refinery") {
      const d = poolDeductionForRefineryLine(line);
      totals[gid].pcs += d.pcs;
      totals[gid].nwt += d.nwt;
      totals[gid].fine_wt += d.fine_wt;
    } else {
      const d = poolDeductionForTaggingLine(line);
      totals[gid].pcs += d.pcs;
      totals[gid].nwt += d.nwt;
    }
  }

  for (const [gidStr, need] of Object.entries(totals)) {
    const gid = Number(gidStr);
    const pool = poolByGroupId[gid] || {};
    const poolPcs = numPool(pool.pool_pcs);
    const poolNwt = numPool(pool.pool_nwt);
    const name = pool.name || pool.label || `Product group #${gid}`;

    const needNwt =
      mode === "refinery" ? numPool(need.fine_wt) || need.nwt : need.nwt;

    if (needNwt > poolNwt && poolNwt >= 0) {
      return {
        valid: false,
        message: `Insufficient untagged weight in product group "${name}" (need ${needNwt.toFixed(3)} g nwt, pool has ${poolNwt.toFixed(3)} g)`,
      };
    }
    if (need.pcs > poolPcs) {
      return {
        valid: false,
        message: `Insufficient untagged pcs in product group "${name}" (need ${need.pcs}, pool has ${poolPcs})`,
      };
    }
  }

  return { valid: true };
}

export function buildPoolByGroupIdFromOptions(options = []) {
  const map = {};
  (options || []).forEach((opt) => {
    const id = Number(opt.value ?? opt.id);
    if (!Number.isFinite(id) || id <= 0) return;
    map[id] = {
      name: opt.label ?? opt.name,
      pool_pcs: opt.pool_pcs,
      pool_nwt: opt.pool_nwt,
    };
  });
  return map;
}

export function enrichProductGroupOption(group, metalGroupsMap = {}) {
  let metalType = "";
  const label = String(group.name || "").toLowerCase();
  const mgData = metalGroupsMap[group.metal_group_id];
  const mgMetal = String(mgData?.metal || "").toLowerCase();

  if (label.includes("gold") || mgMetal.includes("gold")) metalType = "Gold";
  else if (label.includes("silver") || mgMetal.includes("silver")) metalType = "Silver";
  else if (label.includes("platinum") || mgMetal.includes("platinum")) metalType = "Platinum";
  else if (label.includes("diamond") || mgMetal.includes("diamond")) metalType = "Diamond";
  else if (mgData?.metal) {
    metalType = mgData.metal;
  }

  const poolPcs = group.pool_pcs;
  const poolNwt = group.pool_nwt;
  const name = group.name || "";

  return {
    value: group.id ?? group.product_group_id,
    label: name,
    name,
    short_code: group.short_code || group.shortCode || group.code || "",
    metal: metalType,
    metal_group_id: group.metal_group_id,
    item_category_id: group.item_category_id ?? null,
    item_category_name: group.item_category_name || "",
    pool_pcs: numPool(poolPcs),
    pool_nwt: numPool(poolNwt),
    purity: mgData?.purity || "",
    carat: mgData?.carat || "",
  };
}
